import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useMediaQuery } from '@mui/material';
import ProductImages from '../../components/ProductDetails/ProductImages';
import ProductDetailsTabView from '../../components/ProductDetailsTabView';
import { postProduct } from '../../services/products';
import { getManufacturers } from '../../services/manufacturer';
import Dropdown from '../../components/inputFields/DropDown';
import InputField from '../../components/inputFields/InputField';
import TextField from '../../components/inputFields/TextField';
import TextareaField from '../../components/inputFields/TextField';
import Button from '../../components/buttons/Button';
import Breadcrumbs from '../../components/BreadCrumbs';
import { TiDeleteOutline } from 'react-icons/ti';
import { CgAdd } from 'react-icons/cg';
import FormikMultiAsyncDropdown from '../../components/inputFields/FormikMultiAsyncDropdown';
import { searchProduct } from '../../services/search';

/** Default shop for dashboard product create (API requires shop_id). */
const DEFAULT_SHOP_ID = '569c10d1-9be5-4c1a-94ce-ab1892e4b9e8';

const statusOptions = [
    { value: 'IN_STOCK', label: 'In Stock' },
    { value: 'OUT_OF_STOCK', label: 'Out of Stock' },
    { value: 'LIMITED_STOCK', label: 'Limited Stock' },
    { value: 'PREORDER', label: 'Preorder' },
    { value: 'DISCONTINUED', label: 'Discontinued' },
];

const emptyProductDetails = () => ({
    FEATURES: [],
    TECHNICAL_DETAILS: [],
    BENEFITS: [],
    USECASE: [],
    IN_BOX: [],
    ADDITIONAL_DETAILS: [],
});

const initialValues = {
    manufacturer: '',
    shop_sku: '',
    model_no: '',
    aka_name: '',
    meta_description: '',
    meta_keywords: '',
    availability_status: 'IN_STOCK',
    mrp: '',
    discount_percentage: '0',
    product_name: '',
    description: '',
    similar_products: [],
    product_details: emptyProductDetails(),
};

const validationSchema = Yup.object({
    manufacturer: Yup.string().required('Manufacturer is required'),
    availability_status: Yup.string().required('Required'),
    mrp: Yup.number().typeError('Enter a valid number').required('Required'),
    discount_percentage: Yup.number().typeError('Enter a valid number').required('Required'),
    product_name: Yup.string().required('Product name is required'),
});

function ProductCreatePage() {
    const isTab = useMediaQuery('(max-width: 966px)');
    const navigate = useNavigate();
    const [brandOptions, setBrandOptions] = useState([]);
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await getManufacturers('');
                if (res.status === 200 && res.data?.results) {
                    setBrandOptions(res.data.results.map((item) => ({ value: item.id, label: item.name })));
                }
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const calculatePrice = (mrp, discount) => {
        const m = Number(mrp) || 0;
        const d = Number(discount) || 0;
        return Math.round(m - (m * d) / 100);
    };

    const replaceImageAt = (index, file) => {
        setImages((prev) =>
            prev.map((img, i) => (i === index ? { ...img, file, preview: URL.createObjectURL(file) } : img))
        );
    };

    const addImage = (file) => {
        setImages((prev) => [...prev, { id: `${Date.now()}_${Math.random()}`, file, preview: URL.createObjectURL(file) }]);
    };

    const removeImageAt = (img) => {
        setImages((prev) => prev.filter((it) => it.id !== img.id));
    };

    const replaceVideoAt = (index, file) => {
        setVideos((prev) =>
            prev.map((vid, i) => (i === index ? { ...vid, file, preview: URL.createObjectURL(file) } : vid))
        );
    };

    const addVideo = (file) => {
        setVideos((prev) => [...prev, { id: `${Date.now()}_${Math.random()}`, file, preview: URL.createObjectURL(file) }]);
    };

    const removeVideoAt = (vid) => {
        setVideos((prev) => prev.filter((it) => it.id !== vid.id));
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        const simProducts = (values.similar_products || []).map((it) => it.value);
        const hasNewMedia = images.some((it) => it.file) || videos.some((it) => it.file);

        try {
            const payload = {
                shop_id: DEFAULT_SHOP_ID,
                manufacturer: values.manufacturer,
                availability_status: values.availability_status,
                mrp: values.mrp,
                discount_percentage: values.discount_percentage,
                product_name: values.product_name.trim(),
                description: values.description || '',
                product_details: values.product_details || emptyProductDetails(),
                meta_description: values.meta_description || '',
                meta_keywords: values.meta_keywords || '',
                similar_products: simProducts,
            };
            if (values.shop_sku?.trim()) payload.shop_sku = values.shop_sku.trim();
            if (values.model_no?.trim()) payload.model_no = values.model_no.trim();
            if (values.aka_name?.trim()) payload.aka_name = values.aka_name.trim();

            const res = await postProduct(payload);

            if (res.status === 200 || res.status === 201) {
                if (hasNewMedia) {
                    toast.info('Add images and videos from the product edit page after creation.');
                }
                toast.success(res.data?.message || 'Product created successfully');
                const slug = res.data?.slug;
                if (slug) {
                    navigate(`/bazzar/products/${slug}/`);
                } else {
                    navigate('/bazzar/products');
                }
            } else {
                toast.error('Unable to create product');
            }
        } catch (error) {
            console.error('Error creating product:', error);
            toast.error(error?.response?.data?.message || error?.response?.data?.detail || 'Unable to create product');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Breadcrumbs
                items={[
                    { name: 'Home', link: '/dashboard' },
                    { name: 'Bazaar' },
                    { name: 'Products', link: '/bazzar/products' },
                    { name: 'Add product' },
                ]}
            />
            <div className="bg-gray-50 min-h-screen">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, isSubmitting }) => (
                        <Form className="p-4 md:p-8">
                            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold text-gray-900">Add product</h1>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Same structure as product edit: media, basics, pricing, SEO, description, related
                                        products, and detail tabs.
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50"
                                    onClick={() => navigate('/bazzar/products')}
                                >
                                    Cancel
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                <div className="flex flex-col shadow-lg p-4 rounded-lg border bg-white">
                                    <h2 className="text-lg font-semibold mb-2">Images &amp; videos</h2>
                                    <ProductImages
                                        isTab={isTab}
                                        productData={null}
                                        editable
                                        images={images}
                                        videos={videos}
                                        onReplaceImage={replaceImageAt}
                                        onAddImage={addImage}
                                        onRemoveImage={removeImageAt}
                                        onReplaceVideo={replaceVideoAt}
                                        onAddVideo={addVideo}
                                        onRemoveVideo={removeVideoAt}
                                    />
                                    <div className="mt-4">
                                        <Dropdown
                                            name="availability_status"
                                            label="Availability status"
                                            value={values.availability_status}
                                            onChange={(val) => setFieldValue('availability_status', val)}
                                            options={statusOptions}
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col shadow-lg p-4 rounded-lg border bg-white">
                                    <h2 className="text-lg font-semibold mb-2">Basic details</h2>
                                    <div className="grid grid-cols-1 gap-4">
                                        <Dropdown
                                            name="manufacturer"
                                            label="Manufacturer / brand"
                                            value={values.manufacturer}
                                            onChange={(val) => setFieldValue('manufacturer', val)}
                                            options={brandOptions}
                                            needSearch
                                            placeholder="Select manufacturer"
                                        />
                                        <InputField
                                            name="shop_sku"
                                            type="text"
                                            label="Shop SKU"
                                            placeholder="e.g. FA-FK-10"
                                        />
                                        <InputField
                                            name="model_no"
                                            type="text"
                                            label="Model number"
                                            placeholder="e.g. RA-FK-10"
                                        />
                                        <InputField
                                            name="aka_name"
                                            type="text"
                                            label="Short / display name (aka)"
                                            placeholder="Shown in compact lists"
                                        />
                                        <InputField
                                            name="product_name"
                                            type="text"
                                            label="Product name"
                                            required
                                        />
                                    </div>

                                    <div className="mt-4">
                                        <h3 className="text-md font-semibold mb-2">Key features</h3>
                                        <FieldArray name="product_details.FEATURES">
                                            {({ push, remove }) => (
                                                <div className="space-y-2">
                                                    {(values.product_details?.FEATURES || []).map((_, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <span className="checkmark mr-2 bg-primary rounded-full flex-shrink-0">
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="20"
                                                                    height="20"
                                                                    viewBox="0 0 20 20"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M5 10l3 3L15 7"
                                                                        stroke="white"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </span>
                                                            <InputField
                                                                name={`product_details.FEATURES[${index}].value`}
                                                                type="text"
                                                                placeholder="Enter feature"
                                                                className="flex-1"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => remove(index)}
                                                                className="p-2 text-red-500 hover:text-red-700 flex-shrink-0"
                                                            >
                                                                <TiDeleteOutline size={20} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => push({ value: '' })}
                                                        className="inline-flex items-center gap-2 text-primary hover:text-blue-700 mt-2"
                                                    >
                                                        <CgAdd size={18} /> Add feature
                                                    </button>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </div>
                                </div>

                                <div className="flex flex-col shadow-lg p-4 rounded-lg border bg-white">
                                    <h2 className="text-lg font-semibold mb-4 text-center">Pricing</h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <InputField
                                            name="mrp"
                                            type="number"
                                            label="Maximum retail price (MRP)"
                                            required
                                        />
                                        <InputField
                                            name="discount_percentage"
                                            type="number"
                                            label="Discount (%)"
                                            required
                                        />
                                    </div>
                                    <div className="flex justify-end mt-4">
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors w-full disabled:opacity-60"
                                        >
                                            {isSubmitting ? 'Creating…' : 'Create product'}
                                        </Button>
                                    </div>
                                    <div className="bg-gray-100 p-3 rounded-md text-center mt-4">
                                        <span className="block text-sm text-gray-600 mb-1">Calculated price</span>
                                        <span className="text-xl font-bold text-primary">
                                            ₹{calculatePrice(values.mrp, values.discount_percentage)}
                                        </span>
                                        <p className="text-xs text-gray-500 mt-1">Inclusive of all taxes (same as edit screen)</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <TextareaField
                                        name="meta_description"
                                        type="text"
                                        label="Meta description"
                                        placeholder="SEO meta description"
                                    />
                                    <TextareaField
                                        name="meta_keywords"
                                        type="text"
                                        label="Meta keywords"
                                        placeholder="Comma-separated keywords"
                                    />
                                </div>
                            </div>

                            <div className="my-2 mt-4 bg-white shadow-lg p-4 rounded-lg border">
                                <TextField name="description" label="Description" />
                            </div>

                            <div className="my-4 bg-white shadow-lg p-4 rounded-lg border">
                                <FormikMultiAsyncDropdown
                                    name="similar_products"
                                    label="Related / similar products"
                                    needSearch
                                    apiCallFunction={async (input) => {
                                        const response = await searchProduct(input);
                                        const rows = response.data?.results || [];
                                        return rows.map((cat) => ({
                                            value: cat.id,
                                            label: cat.product_name,
                                        }));
                                    }}
                                    placeholder="Search products to link…"
                                />
                            </div>

                            <div className="bg-white shadow-lg p-4 rounded-lg border">
                                <h2 className="text-lg font-semibold mb-3">Product details (tabs)</h2>
                                <ProductDetailsTabView productDetails={values.product_details} editable />
                            </div>

                            <div className="mt-6 flex flex-wrap gap-3">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary-dark disabled:opacity-60"
                                >
                                    {isSubmitting ? 'Creating…' : 'Create product'}
                                </Button>
                                <Button
                                    type="button"
                                    className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50"
                                    onClick={() => navigate('/bazzar/products')}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
}

export default ProductCreatePage;
